/**
 * Receipt v2: the pipeline's provenance record riding on card.payload.receipt, decoded
 * defensively. The payload crosses a wire we do not control, so every part of the shape is
 * treated as potentially missing or malformed: junk degrades to null (or is dropped from a
 * list) and never throws. A card without a receipt renders exactly as before, so the whole
 * feature is opt-in per card by construction.
 */

export type ReceiptSource = {
  name: string;
  url: string;
  domain: string;
  tier: string | null;
};

export type ReceiptClaims = {
  checked: boolean;
  flagged: { claim: string; why: string }[];
  resolvedByEdit?: boolean;
};

export type Receipt = {
  sources: ReceiptSource[];
  corroboration: number | null;
  discoveredVia: string | null;
  checkedAt: string | null;
  claims: ReceiptClaims | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringOrNull(value: unknown): string | null {
  return typeof value === "string" && value.length ? value : null;
}

function decodeSources(value: unknown): ReceiptSource[] {
  if (!Array.isArray(value)) return [];
  const out: ReceiptSource[] = [];
  for (const entry of value) {
    if (!isRecord(entry)) continue;
    const { name, url, domain } = entry;
    if (typeof name !== "string" || typeof url !== "string" || typeof domain !== "string") continue;
    out.push({ name, url, domain, tier: stringOrNull(entry.tier) });
  }
  return out;
}

function decodeClaims(value: unknown): ReceiptClaims | null {
  // claims.checked must be a real boolean; anything else means the block cannot be
  // trusted (a "yes" string is a pipeline bug, not a claim check), so it all goes.
  if (!isRecord(value) || typeof value.checked !== "boolean") return null;
  const flagged: { claim: string; why: string }[] = [];
  if (Array.isArray(value.flagged)) {
    for (const f of value.flagged) {
      if (isRecord(f) && typeof f.claim === "string" && typeof f.why === "string") {
        flagged.push({ claim: f.claim, why: f.why });
      }
    }
  }
  const claims: ReceiptClaims = { checked: value.checked, flagged };
  if (typeof value.resolvedByEdit === "boolean") claims.resolvedByEdit = value.resolvedByEdit;
  return claims;
}

/** The card's receipt, validated, or null when there is none worth rendering. */
export function receiptOf(
  card: { payload?: Record<string, unknown> | null } | null | undefined,
): Receipt | null {
  const raw = card?.payload?.receipt;
  if (!isRecord(raw)) return null;
  return {
    sources: decodeSources(raw.sources),
    corroboration: typeof raw.corroboration === "number" && Number.isFinite(raw.corroboration) ? raw.corroboration : null,
    discoveredVia: stringOrNull(raw.discoveredVia),
    checkedAt: stringOrNull(raw.checkedAt),
    claims: decodeClaims(raw.claims),
  };
}

/**
 * The card's Must Know standing (payload.mustKnow), validated: rank must be a real 1-3
 * integer or the whole block is ignored; a junk reason degrades to "" (the stamp still
 * earns its place, it just has no title). The same defensive posture as receiptOf.
 */
export function mustKnowOf(
  card: { payload?: Record<string, unknown> | null } | null | undefined,
): { rank: number; reason: string } | null {
  const raw = card?.payload?.mustKnow;
  if (!isRecord(raw)) return null;
  const rank = raw.rank;
  if (typeof rank !== "number" || !Number.isInteger(rank) || rank < 1 || rank > 3) return null;
  return { rank, reason: typeof raw.reason === "string" ? raw.reason : "" };
}

/** Where the claim check landed: ran clean, ran and flagged something, or never ran. */
export function claimStatus(r: Receipt): "clean" | "flagged" | "unchecked" {
  if (!r.claims?.checked) return "unchecked";
  return r.claims.flagged.length ? "flagged" : "clean";
}

/**
 * A quiet humanised "Checked against source 2 h ago" line, or null when the receipt
 * carries no parseable timestamp. A clock skewed into the future reads as "just now",
 * never as negative time.
 */
export function lastCheckedLabel(r: Receipt, now: Date = new Date()): string | null {
  if (!r.checkedAt) return null;
  const at = Date.parse(r.checkedAt);
  if (!Number.isFinite(at)) return null;
  const minutes = Math.floor((now.getTime() - at) / 60000);
  const when =
    minutes < 1 ? "just now"
    : minutes < 60 ? `${minutes} min ago`
    : minutes < 48 * 60 ? `${Math.floor(minutes / 60)} h ago`
    : `${Math.floor(minutes / (24 * 60))} d ago`;
  return `Checked against source ${when}`;
}
